/**
 * PRE-IMPORT REVIEW COMPONENT
 * 
 * Displays data quality issues found before import and lets users
 * choose how to handle them. Gives users control while guiding
 * them toward cleaner data.
 * 
 * PHILOSOPHY:
 * - Inform, don't block
 * - Offer solutions, not just problems
 * - Explain why it matters
 * - Let users proceed with "I know what I'm doing"
 */

import { useState, useMemo } from 'react';
import {
  AlertTriangle, AlertCircle, Info, CheckCircle2,
  ChevronDown, ChevronRight, Sparkles, ArrowRight
} from 'lucide-react';
import {
  analyzePreImport,
  applyFixes,
  getIssueIcon,
  formatCount,
  type FixAction,
  type IssueSeverity
} from '../utils/preImportAnalysis';

interface PreImportReviewProps {
  mappedRows: Record<string, any>[];
  onProceed: (fixedRows: Record<string, any>[]) => void;
  onBack: () => void;
}

export default function PreImportReview({ 
  mappedRows, 
  onProceed, 
  onBack 
}: PreImportReviewProps) {
  // Analyze the data
  const analysis = useMemo(() => analyzePreImport(mappedRows), [mappedRows]);
  
  // Track which fixes the user has selected
  const [selectedFixes, setSelectedFixes] = useState<Record<string, FixAction>>(() => {
    // Default: apply suggested fix for auto-fixable issues
    const defaults: Record<string, FixAction> = {};
    for (const issue of analysis.issues) {
      defaults[issue.id] = issue.canAutoFix ? issue.suggestedFix : 'keep';
    }
    return defaults;
  });
  
  // Track expanded issues
  const [expandedIssues, setExpandedIssues] = useState<Set<string>>(new Set());
  
  // Calculate summary stats
  const fixCounts = useMemo(() => {
    let willFix = 0;
    let willKeep = 0;
    
    for (const issue of analysis.issues) {
      const action = selectedFixes[issue.id];
      if (action && action !== 'keep') {
        willFix += issue.totalCount;
      } else {
        willKeep += issue.totalCount;
      }
    }
    
    return { willFix, willKeep };
  }, [analysis.issues, selectedFixes]);
  
  // Toggle issue expansion
  const toggleExpanded = (issueId: string) => {
    setExpandedIssues(prev => {
      const next = new Set(prev);
      if (next.has(issueId)) {
        next.delete(issueId);
      } else {
        next.add(issueId);
      }
      return next;
    });
  };
  
  // Update fix selection
  const setFixAction = (issueId: string, action: FixAction) => {
    setSelectedFixes(prev => ({ ...prev, [issueId]: action }));
  };
  
  // Handle proceed - apply fixes and continue
  const handleProceed = () => {
    const fixedRows = applyFixes(mappedRows, analysis.issues, selectedFixes);
    onProceed(fixedRows);
  };
  
  // Get severity styling
  const getSeverityStyles = (severity: IssueSeverity) => {
    switch (severity) {
      case 'error':
        return {
          bg: 'bg-red-500/10',
          border: 'border-red-500/30',
          icon: 'text-red-400',
          badge: 'bg-red-500/20 text-red-300',
        };
      case 'warning':
        return {
          bg: 'bg-amber-500/10',
          border: 'border-amber-500/30',
          icon: 'text-amber-400',
          badge: 'bg-amber-500/20 text-amber-300',
        };
      case 'info':
        return {
          bg: 'bg-blue-500/10',
          border: 'border-blue-500/30',
          icon: 'text-blue-400',
          badge: 'bg-blue-500/20 text-blue-300',
        };
    }
  };
  
  const SeverityIcon = ({ severity }: { severity: IssueSeverity }) => {
    switch (severity) {
      case 'error':
        return <AlertCircle className="w-5 h-5" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      case 'info':
        return <Info className="w-5 h-5" />;
    }
  };

  // If no issues, show clean bill of health
  if (analysis.issues.length === 0) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Your Data Looks Great! 🎉
          </h2>
          <p className="text-slate-400">
            No issues found in {formatCount(mappedRows.length, 'item')}.
            Ready to import!
          </p>
        </div>
        
        {/* Actions */}
        <div className="flex justify-center gap-4">
          <button
            onClick={onBack}
            className="px-6 py-3 text-slate-400 hover:text-white transition-colors"
          >
            ← Back to Preview
          </button>
          <button
            onClick={() => onProceed(mappedRows)}
            className="px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all flex items-center gap-2"
          >
            Import {formatCount(mappedRows.length, 'Item')}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-8 h-8 text-amber-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          Let's Clean Up Your Data
        </h2>
        <p className="text-slate-400 max-w-lg mx-auto">
          We found a few things that might need attention. Clean data means better search,
          filters, and stats for your collection!
        </p>
      </div>
      
      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-slate-800/50 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-white">{mappedRows.length}</div>
          <div className="text-sm text-slate-400">Total Items</div>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-green-400">{analysis.cleanRows}</div>
          <div className="text-sm text-slate-400">Clean Items</div>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-amber-400">{analysis.rowsWithIssues}</div>
          <div className="text-sm text-slate-400">Need Review</div>
        </div>
      </div>
      
      {/* Issues List */}
      <div className="space-y-4">
        {analysis.issues.map(issue => {
          const styles = getSeverityStyles(issue.severity);
          const isExpanded = expandedIssues.has(issue.id);
          const currentAction = selectedFixes[issue.id];
          
          return (
            <div 
              key={issue.id}
              className={`rounded-xl border ${styles.border} ${styles.bg} overflow-hidden`}
            >
              {/* Issue Header */}
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => toggleExpanded(issue.id)}
              >
                <div className="flex items-center gap-3">
                  <span className={styles.icon}>
                    <SeverityIcon severity={issue.severity} />
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getIssueIcon(issue.type)}</span>
                      <span className="font-semibold text-white">{issue.title}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${styles.badge}`}>
                        {issue.totalCount} {issue.totalCount === 1 ? 'item' : 'items'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-400 mt-0.5">{issue.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {/* Quick action indicator */}
                  {currentAction !== 'keep' && (
                    <span className="text-xs px-2 py-1 bg-green-500/20 text-green-300 rounded-full">
                      Will fix
                    </span>
                  )}
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </div>
              
              {/* Expanded Content */}
              {isExpanded && (
                <div className="border-t border-slate-700/50 p-4 space-y-4">
                  {/* Example values */}
                  <div>
                    <div className="text-sm font-medium text-slate-300 mb-2">Examples found:</div>
                    <div className="flex flex-wrap gap-2">
                      {issue.exampleValues.map((value, i) => (
                        <code 
                          key={i}
                          className="px-2 py-1 bg-slate-700/50 rounded text-sm text-slate-300"
                        >
                          "{value}"
                        </code>
                      ))}
                      {issue.totalCount > issue.exampleValues.length && (
                        <span className="text-sm text-slate-500">
                          +{issue.totalCount - issue.exampleValues.length} more
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Fix options */}
                  <div>
                    <div className="text-sm font-medium text-slate-300 mb-2">What would you like to do?</div>
                    <div className="space-y-2">
                      {/* Suggested fix option */}
                      {issue.canAutoFix && (
                        <label 
                          className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                            currentAction === issue.suggestedFix 
                              ? 'bg-green-500/10 border border-green-500/30' 
                              : 'bg-slate-700/30 border border-transparent hover:bg-slate-700/50'
                          }`}
                        >
                          <input
                            type="radio"
                            name={`fix-${issue.id}`}
                            checked={currentAction === issue.suggestedFix}
                            onChange={() => setFixAction(issue.id, issue.suggestedFix)}
                            className="mt-0.5"
                          />
                          <div>
                            <div className="font-medium text-white flex items-center gap-2">
                              ✨ {issue.fixDescription}
                              <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-300 rounded-full">
                                Recommended
                              </span>
                            </div>
                            <div className="text-sm text-slate-400">
                              This helps your collection work better with search and filters.
                            </div>
                          </div>
                        </label>
                      )}
                      
                      {/* Keep as-is option */}
                      <label 
                        className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          currentAction === 'keep' 
                            ? 'bg-slate-600/30 border border-slate-500/30' 
                            : 'bg-slate-700/30 border border-transparent hover:bg-slate-700/50'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`fix-${issue.id}`}
                          checked={currentAction === 'keep'}
                          onChange={() => setFixAction(issue.id, 'keep')}
                          className="mt-0.5"
                        />
                        <div>
                          <div className="font-medium text-white">
                            Keep as-is
                          </div>
                          <div className="text-sm text-slate-400">
                            Import with the original values. You can clean up later in Settings → Data Management.
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Why This Matters */}
      <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
        <h3 className="font-semibold text-purple-300 mb-2 flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          Why Clean Data Matters
        </h3>
        <ul className="text-sm text-slate-300 space-y-1">
          <li>🔍 <strong>Better Search:</strong> Find polishes by collection, event, or season</li>
          <li>📊 <strong>Accurate Stats:</strong> See breakdowns by brand, year, and more</li>
          <li>👥 <strong>Community Features:</strong> Share organized collections with others</li>
          <li>🔮 <strong>Future Features:</strong> AI recommendations and trend analysis</li>
        </ul>
      </div>
      
      {/* Summary & Actions */}
      <div className="bg-slate-800/50 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="text-slate-300">
            {fixCounts.willFix > 0 ? (
              <span>
                <strong className="text-green-400">{fixCounts.willFix}</strong> items will be cleaned up
                {fixCounts.willKeep > 0 && (
                  <span>, <strong className="text-slate-400">{fixCounts.willKeep}</strong> kept as-is</span>
                )}
              </span>
            ) : (
              <span>All issues will be imported as-is</span>
            )}
          </div>
        </div>
        
        <div className="flex justify-between gap-4">
          <button
            onClick={onBack}
            className="px-6 py-3 text-slate-400 hover:text-white transition-colors"
          >
            ← Back to Preview
          </button>
          <button
            onClick={handleProceed}
            className="px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all flex items-center gap-2"
          >
            {fixCounts.willFix > 0 ? 'Fix & Import' : 'Import'} {formatCount(mappedRows.length, 'Item')}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
